/**
 * Subscription Overview Page
 *
 * Manifold observability: subscription status distribution, MRR,
 * top plans, activation trends, and cancel reasons from chargebee.Subscriptions.
 */

import { useState, useEffect, useCallback } from 'react'
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts'
import { manifoldAPI } from '../services/api'
import {
  Card, CardContent, CardHeader, CardTitle,
  Button
} from '../components/ui'
import {
  RefreshCw, Loader2, AlertCircle, Users, DollarSign, TrendingUp, UserMinus
} from 'lucide-react'

const STATUS_COLORS = {
  active: '#22c55e',
  cancelled: '#ef4444',
  non_renewing: '#f59e0b',
  paused: '#8b5cf6',
  future: '#3b82f6',
  in_trial: '#06b6d4',
}

function formatNumber(value) {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US').format(value)
}

function formatCurrency(cents) {
  if (cents === null || cents === undefined) return '-'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(cents / 100)
}

export default function SubscriptionOverviewPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await manifoldAPI.getSubscriptions()
      if (response.data.success) {
        setData(response.data.data)
      }
    } catch (err) {
      console.error('[SUBSCRIPTIONS] Error:', err)
      setError(err.response?.data?.error?.message || err.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const totalSubs = data?.status_distribution?.reduce((s, d) => s + d.count, 0) || 0
  const activeSubs = data?.mrr?.active_count || 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Subscription Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">Chargebee subscription status, MRR, plans, and churn reasons</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <Users className="w-3.5 h-3.5" /> Total Subscriptions
                </div>
                <div className="text-xl font-bold">{formatNumber(totalSubs)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <TrendingUp className="w-3.5 h-3.5 text-green-500" /> Active
                </div>
                <div className="text-xl font-bold text-green-600">{formatNumber(activeSubs)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <DollarSign className="w-3.5 h-3.5" /> MRR
                </div>
                <div className="text-xl font-bold">{formatCurrency(data.mrr?.total)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-3">
                <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium mb-1">
                  <DollarSign className="w-3.5 h-3.5" /> Avg MRR/Sub
                </div>
                <div className="text-xl font-bold">
                  {activeSubs > 0 ? formatCurrency(Math.round(data.mrr?.total / activeSubs)) : '-'}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Distribution Pie */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Status Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.status_distribution}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        outerRadius={90}
                        label={({ status, count }) => `${status} (${formatNumber(count)})`}
                        labelLine={false}
                      >
                        {data.status_distribution?.map((entry) => (
                          <Cell key={entry.status} fill={STATUS_COLORS[entry.status] || '#94a3b8'} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatNumber(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-1">
                  {data.status_distribution?.map((s) => (
                    <div key={s.status} className="flex items-center gap-2 text-xs">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[s.status] || '#94a3b8' }} />
                      <span className="text-muted-foreground">{s.status}:</span>
                      <span className="font-medium">{formatNumber(s.count)}</span>
                      {s.mrr > 0 && <span className="text-muted-foreground">({formatCurrency(s.mrr)} MRR)</span>}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Cancel Reasons */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <UserMinus className="w-4 h-4" /> Cancellation Reasons
                </CardTitle>
              </CardHeader>
              <CardContent>
                {data.cancel_reasons && data.cancel_reasons.length > 0 ? (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.cancel_reasons} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis dataKey="reason" type="category" width={140} tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Bar dataKey="count" fill="#ef4444" name="Cancellations" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground py-8 text-center">No cancellation data available</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Monthly Activations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Monthly Activations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.monthly_activations}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(2)} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="activated" fill="#3b82f6" name="Activated" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top Plans */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Top Plans (Active/Non-Renewing)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border text-left">
                      <th className="pb-2 font-medium text-muted-foreground">Plan</th>
                      <th className="pb-2 font-medium text-muted-foreground text-right">Subscribers</th>
                      <th className="pb-2 font-medium text-muted-foreground text-right">Unit Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_plans?.map((plan) => (
                      <tr key={plan.plan} className="border-b border-border/50">
                        <td className="py-2 font-mono text-xs">{plan.plan}</td>
                        <td className="py-2 text-right">{formatNumber(plan.subscribers)}</td>
                        <td className="py-2 text-right">{plan.unit_price != null ? formatCurrency(plan.unit_price) : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
